// Reusable employee create/edit modal for the Management → Employees database. On save it calls
// onSaved() so the caller can close/refresh. Active employees are carried onto the Overtime roster
// (handled server-side).

import { useState, type InputHTMLAttributes } from 'react';
import { EMPLOYEE_TEAMS, type EmployeeFieldsInput } from '@technohull/shared';
import { useCreateEmployee, useUpdateEmployee } from '../features/employees.js';
import { InlineError, Modal } from './ui.js';

type Fields = EmployeeFieldsInput;

const EMPTY: Fields = {
  name: '', employeeNo: '', teams: [], department: '', jobTitle: '',
  startDate: '', phone: '', email: '', active: true,
};

// the phone-keyboard hints a field can carry (keypad, autofill, capitalisation, the return key's label)
type InputHints = Pick<InputHTMLAttributes<HTMLInputElement>,
  'inputMode' | 'autoComplete' | 'autoCapitalize' | 'autoCorrect' | 'spellCheck' | 'enterKeyHint' | 'onBlur' | 'aria-invalid'>;

// Module-level (stable) field component — defining it inside the modal would remount on every
// keystroke and drop input focus.
function TF({ label, value, onChange, type, wide, ...hints }: { label: string; value: string; onChange: (v: string) => void; type?: string; wide?: boolean } & InputHints) {
  return (
    <label style={{ display: 'block', gridColumn: wide ? '1 / -1' : undefined }}>
      <span className="field-label">{label}</span>
      <input className="field" type={type ?? 'text'} style={{ width: '100%' }} value={value} onChange={(e) => onChange(e.target.value)} {...hints} />
    </label>
  );
}

export function EmployeeFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial?: Partial<Fields> & { id?: number };
  onClose: () => void;
  onSaved?: () => void;
}) {
  const editId = initial?.id ?? null;
  const [f, setF] = useState<Fields>(() => ({ ...EMPTY, ...initial }) as Fields);
  const create = useCreateEmployee();
  const update = useUpdateEmployee();
  const busy = create.isPending || update.isPending;
  const err = create.error || update.error;

  const set = <K extends keyof Fields>(k: K, v: Fields[K]) => setF((p) => ({ ...p, [k]: v }));
  const valid = !!f.name.trim();
  // the missing-name hint waits until the name field has been left (or a save tried), so a fresh form
  // doesn't open on an error
  const [nameTouched, setNameTouched] = useState(false);
  const showNameErr = !valid && nameTouched;

  const save = () => {
    setNameTouched(true);
    if (!valid) return;
    const payload = { ...f, name: f.name.trim() };
    if (editId) update.mutate({ ...payload, id: editId }, { onSuccess: () => { onSaved?.(); onClose(); } });
    else create.mutate(payload, { onSuccess: () => { onSaved?.(); onClose(); } });
  };

  return (
    <Modal
      title={editId ? 'Edit employee' : 'New employee'}
      onClose={onClose}
      width={640}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!valid || busy} onClick={save}>
            {busy ? 'Saving…' : editId ? 'Save changes' : 'Create employee'}
          </button>
        </>
      }
    >
      {err && <InlineError error={err} onDismiss={() => { create.reset(); update.reset(); }} />}

      <div className="emp-form-grid">
        <TF label="Full name" value={f.name} onChange={(v) => set('name', v)} wide
          autoComplete="name" autoCapitalize="words" onBlur={() => setNameTouched(true)} aria-invalid={showNameErr || undefined} />
        <TF label="Employee no." value={f.employeeNo} onChange={(v) => set('employeeNo', v)}
          autoComplete="off" autoCapitalize="characters" autoCorrect="off" spellCheck={false} />
        <TF label="Department" value={f.department} onChange={(v) => set('department', v)} autoCapitalize="words" />
        <TF label="Job title" value={f.jobTitle} onChange={(v) => set('jobTitle', v)} autoComplete="organization-title" autoCapitalize="sentences" />
        <TF label="Start date" value={f.startDate} onChange={(v) => set('startDate', v)} type="date" />
        <TF label="Phone" value={f.phone} onChange={(v) => set('phone', v)} type="tel" inputMode="tel" autoComplete="tel" />
        <TF label="Email" value={f.email} onChange={(v) => set('email', v)} type="email" inputMode="email" autoComplete="email"
          autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="done" />
        <div style={{ gridColumn: '1 / -1' }} role="group" aria-labelledby="emp-teams-lbl">
          <div id="emp-teams-lbl" className="field-label">Teams</div>
          <div className="emp-teams">
            {EMPLOYEE_TEAMS.map((t) => {
              const on = f.teams.includes(t);
              return (
                <label key={t} className={`emp-team-chip${on ? ' on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => set('teams', on ? f.teams.filter((x) => x !== t) : [...f.teams, t])} />
                  {t}
                </label>
              );
            })}
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>Tick every team this person works in.</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} />
            Active (appears on the Overtime roster)
          </label>
        </div>
      </div>
      {showNameErr && <div style={{ color: 'var(--err-fg)', fontSize: 11, marginTop: 8 }}>Enter a name.</div>}
    </Modal>
  );
}
